'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import Sessions from './sessions'
import SessionDetailsComponent from './sessionDetailsComponent'
import { User, Session, SessionDetails, SubscriptionEnum } from '../lib/types'
import UserComponent from './users'
import { ModeToggle } from './lib/mode-toggle'
import Bookmarks from './bookmarks'
import { getSessionDetails, getUsers, getUserSessions, signOut } from '../lib/api'
import { Button } from './ui/button'
import { useNavigate } from 'react-router-dom'
import { cookieCleanup } from '../lib/utils'


export default function Dashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionType, setSessionType] = useState<SubscriptionEnum>(SubscriptionEnum.RESEARCH);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('users');
  const navigate = useNavigate();

  const refreshUsers = useCallback(async () => {
      const fetchUsers = async () => {
        const response = await getUsers();
        setUsers(response);
      };
      fetchUsers();
  }, []);

  useEffect(() => {
    refreshUsers()
  },[refreshUsers]);

  const showUserSessions = useCallback(async (user: User) => {
    const fetchSessions = async () => {
      const response = await getUserSessions(user.user_id, sessionType);
      if(response === "NO_DATA_FOUND"){
        setSessions([]);
      } else {
        setSessions(response);
      }
      setSelectedTab('sessions');
      setSelectedUser(user);
    };
    fetchSessions();
  }, [sessionType]);

  const refreshUserSessions = useCallback(async () => {
    const fetchSessions = async () => {
      if(selectedUser?.user_id){
        const response = await getUserSessions(selectedUser.user_id, sessionType);
        if(response === "NO_DATA_FOUND"){ 
          setSessions([]);
        } else {
          setSessions(response);
        }
      }
    }
      fetchSessions();
  }, [selectedUser, sessionType]);

  const showSessionsDetails = useCallback(async (session: Session) => {
    const fetchSessionDetails = async () => {
      const data = await getSessionDetails(session.session_id);
      setSessionDetails(data as SessionDetails);
      setSelectedTab('sessionDetails');
      setSelectedSession(session);
    };
    fetchSessionDetails();
  }, []);

  const refreshSessionsDetails = useCallback(async () => {
    const fetchSessionDetails = async () => {
      if(selectedSession?.session_id){
        const data = await getSessionDetails(selectedSession.session_id);
        setSessionDetails(data as SessionDetails);
      }
    };
    fetchSessionDetails();
  }, [selectedSession]);

  const UserComp = useMemo(() => {
    return <UserComponent users={users} refreshUsers={refreshUsers} showUserSessions={showUserSessions} />;
  }, [users, refreshUsers, showUserSessions]);

  const SessionsComp = useMemo(() => {
    return <Sessions sessions={sessions} showSessionsDetails={showSessionsDetails} selectedUser={selectedUser} refreshUserSessions={refreshUserSessions} sessionType={sessionType} setSessionType={setSessionType}/>;
  }, [sessions, showSessionsDetails, selectedUser, refreshUserSessions, sessionType, setSessionType]);

  const handleSignout = useCallback(()=> {
    const userString = localStorage.getItem("user");
    let userEmail = "";

    try{
        const user: User = JSON.parse(userString || ""); 
        userEmail = user.user_email || "";
        signOut(userEmail);

        // Remove the invalid cookie
        cookieCleanup();
        
        navigate(0);
    } catch(error) {
      console.error("No user found")
    }
  },[navigate])

  return (
    <div className="ml-0 mr-0 p-1 h-screen overflow-hidden">
      <Tabs defaultValue="users" value={selectedTab} onValueChange={setSelectedTab} className="h-full flex flex-col">
        <TabsList className='justify-start'>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="sessionDetails">Session Details</TabsTrigger>
          <TabsTrigger value="bookmarks">Bookmarks</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <div className="flex-1 overflow-hidden">
          <TabsContent value="users" className="h-full">
            {UserComp}
          </TabsContent>
          <TabsContent value="sessions" className="h-full">
            {SessionsComp}
          </TabsContent>
          <TabsContent value="sessionDetails" id='ssd-tab' className="h-full">
            <SessionDetailsComponent sessionDetails={sessionDetails} refreshSessionsDetails={refreshSessionsDetails} selectedUser={selectedUser}/>
          </TabsContent>
          <TabsContent value="bookmarks" className="h-full">
            <Bookmarks selectedUser={selectedUser} sessionType={sessionType} setSessionType={setSessionType}/>
          </TabsContent>
          <TabsContent value="settings" className="h-full"> 
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
                <CardDescription>Manage settings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 pl-8">
                  <ModeToggle />
                  <Button onClick={handleSignout} type="button" variant="secondary" className='w-36'>Sign out</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}